import { useCallback, useEffect, useRef, useState } from 'react';
import { ContextMenu } from 'radix-ui';
import { maskViewLayerId, planChannelView } from '../core/channels';
import { guideAt, type Guide } from '../core/guides';
import { snapPoint } from '../core/snap';
import type { Point, ToolId } from '../core/types';
import { getEngine } from '../engine/engine';
import { WebGL2UnsupportedError } from '../gl/context';
import { documentToScreen, screenToDocument, type CanvasSize } from '../gl/view';
import {
  activeSnapTargets,
  beginMetaTransaction,
  commitMetaTransaction,
  cropToSelection,
  deselect,
  moveGuideTransient,
  panBy,
  placeImageAsLayer,
  removeGuideTransient,
  selectAll,
  zoomStep,
} from '../state/actions';
import { acceptDroppedFile } from '../state/openFiles';
import { useHudStore } from '../state/hud';
import { liveRender } from '../state/renderState';
import { installEngineBridge } from '../state/engineBridge';
import { commandChord, handleKeymap, isAppChord, isTypingTarget } from '../state/keymap';
import { getActiveDocument, getPaintOptions, useAppStore } from '../state/store';
import { TOOLS } from '../tools';
import type { Tool, ToolContext, ToolPointerEvent } from '../tools/types';
import { startTransformSelection } from '../tools/transform';
import { droppedFiles } from '../io/import';
import { setInternalClipboard } from '../io/clipboard';
import { drawGrid, drawGuides } from './canvasFurniture';
import { Rulers } from './Rulers';
import { TextEditOverlay } from './TextEditOverlay';
import { buildToolContextSections, ToolContextMenuItems } from './toolContextMenu';
import {
  TRANSFORM_MODES,
  QUICK_TRANSFORMS,
  applyQuickTransform,
  beginTransform,
} from './transformMenu';

const ANTS_STEP = 1;
/** Screen-pixel radius for snapping a drag and for grabbing a guide. */
const SNAP_TOLERANCE = 6;
const GUIDE_GRAB = 5;

/** Paint tools where Alt temporarily becomes the eyedropper (not clone/heal source). */
const ALT_SAMPLE_TOOLS = new Set<ToolId>([
  'brush',
  'pencil',
  'eraser',
  'blur',
  'sharpen',
  'smudge',
  'dodge',
  'burn',
  'sponge',
  'spotHealing',
]);

interface PointerLike {
  clientX: number;
  clientY: number;
  shiftKey: boolean;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  button: number;
  pressure?: number;
  pointerType?: string;
}

export function CanvasView() {
  const containerRef = useRef<HTMLDivElement>(null);
  const glCanvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const activeToolRef = useRef<Tool | null>(null);
  const paintingRef = useRef(false);
  const panDragRef = useRef<Point | null>(null);
  const spaceHeldRef = useRef(false);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [altHeld, setAltHeld] = useState(false);
  const [dropActive, setDropActive] = useState(false);
  const [canvasSize, setCanvasSize] = useState<CanvasSize>({ width: 0, height: 0 });
  const guideDragRef = useRef<Guide | null>(null);

  const engine = getEngine();
  const tool = useAppStore((state) => state.tool);
  const transformTarget = useAppStore((state) => state.transformTarget);
  const activeDocumentId = useAppStore((state) => state.activeDocumentId);
  const showRulers = useAppStore((state) => state.showRulers);
  const doc = useAppStore(getActiveDocument);
  const view = useAppStore((state) =>
    state.activeDocumentId ? (state.views[state.activeDocumentId] ?? null) : null,
  );
  const hudCursor = useHudStore((state) => state.cursor);

  const buildToolContext = useCallback((): ToolContext | null => {
    if (!engine.available) return null;
    const store = useAppStore.getState();
    const doc = getActiveDocument(store);
    const view = doc ? store.views[doc.id] : null;
    if (!doc || !view) return null;
    return {
      engine,
      doc,
      view,
      options: store.toolOptions,
      paint: getPaintOptions(store),
      paintTarget: store.paintTarget,
      activeChannel: store.activeChannel,
      gradient: store.gradient,
      foreground: store.foreground,
      background: store.background,
      requestRender: () => engine.invalidate(),
      setStatus: store.setStatus,
    };
  }, [engine]);

  const drawOverlay = useCallback(() => {
    const canvas = overlayRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const dpr = engine.devicePixelRatio;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);

    const context = buildToolContext();
    if (!context) return;
    const store = useAppStore.getState();
    const toScreen = (point: Point) =>
      documentToScreen(point, context.view, engine.canvasSize);
    const furniture = {
      ctx,
      toScreen,
      zoom: context.view.zoom,
      width: engine.canvasSize.width,
      height: engine.canvasSize.height,
    };

    // Grid and guides sit under the tool's own overlay, as they do in Photoshop.
    if (store.showGrid) drawGrid(furniture, context.doc, store.grid);
    if (store.showGuides) drawGuides(furniture, context.doc.guides, store.guidesLocked);

    const current = activeToolRef.current;
    current?.drawOverlay?.({ ctx, zoom: context.view.zoom, toScreen }, context);
  }, [buildToolContext, engine]);

  const draw = useCallback(() => {
    const store = useAppStore.getState();
    const doc = getActiveDocument(store);
    const view = doc ? store.views[doc.id] : null;

    if (!doc || !view) {
      engine.clearCanvas();
      drawOverlay();
      return;
    }

    engine.renderDocument(doc, {
      view,
      overrides: liveRender.overrides,
      maskOverrides: liveRender.maskOverrides,
      channelOverrides: liveRender.channelOverrides,
      transform: liveRender.transform,
      selectionTransform: liveRender.selectionTransform,
      showSelection: liveRender.showSelection,
      antsPhase: liveRender.antsPhase,
      channels: planChannelView(doc, store.maskView, maskViewLayerId(doc, store.activeChannel)),
    });
    drawOverlay();

    if (doc.hasSelection) {
      liveRender.antsPhase = (liveRender.antsPhase + ANTS_STEP) % 512;
      engine.invalidate('overlay');
    }
  }, [drawOverlay, engine]);

  useEffect(() => {
    const canvas = glCanvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    try {
      engine.attach(canvas);
    } catch (error) {
      useAppStore
        .getState()
        .setFatalError(
          error instanceof WebGL2UnsupportedError
            ? error.message
            : 'Failed to initialize WebGL2',
        );
      return;
    }

    installEngineBridge();

    const syncSize = () => {
      const rect = container.getBoundingClientRect();
      engine.resizeCanvas(rect.width, rect.height);
      setCanvasSize({ width: rect.width, height: rect.height });
      const overlay = overlayRef.current;
      if (overlay) {
        const dpr = engine.devicePixelRatio;
        overlay.width = Math.round(rect.width * dpr);
        overlay.height = Math.round(rect.height * dpr);
      }
      engine.invalidate();
    };

    const observer = new ResizeObserver(syncSize);
    observer.observe(container);
    syncSize();

    return () => observer.disconnect();
  }, [engine]);

  useEffect(() => {
    engine.setFrameCallback(draw);
    engine.invalidate();
    return () => engine.setFrameCallback(null);
  }, [draw, engine]);

  useEffect(() => useAppStore.subscribe(() => engine.invalidate()), [engine]);

  useEffect(() => {
    const context = buildToolContext();
    const previous = activeToolRef.current;
    if (previous && context) previous.cancel?.(context);
    const next = TOOLS[tool];
    activeToolRef.current = next;
    if (context) next.activate?.(context);
    engine.invalidate();
    // `transformTarget` is a dependency so that asking for Transform Selection
    // while the transform tool is already active restarts its session.
  }, [tool, transformTarget, activeDocumentId, buildToolContext, engine]);

  const toToolEvent = useCallback(
    (event: PointerLike, context: ToolContext): ToolPointerEvent | null => {
      const canvas = overlayRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      const screen = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      const pointerType: ToolPointerEvent['pointerType'] =
        event.pointerType === 'pen' || event.pointerType === 'touch' ? event.pointerType : 'mouse';
      const raw = screenToDocument(screen, context.view, engine.canvasSize);
      // Tools whose pointer *is* the geometry get snapping for free; a brush
      // must never be pulled off the pointer, so it opts out.
      const snapped =
        activeToolRef.current?.snaps && !event.altKey
          ? snapPoint(raw, activeSnapTargets(), SNAP_TOLERANCE / context.view.zoom)
          : raw;
      return {
        screen,
        point: snapped,
        rawPoint: raw,
        pressure: pointerType === 'pen' && event.pressure && event.pressure > 0 ? event.pressure : 1,
        pointerType,
        button: event.button,
        shift: event.shiftKey,
        alt: event.altKey,
        ctrl: event.ctrlKey || event.metaKey,
      };
    },
    [engine],
  );

  type PointerHandler = (event: ToolPointerEvent, ctx: ToolContext) => void;

  const dispatch = useCallback(
    (event: PointerLike, pick: (tool: Tool) => PointerHandler | undefined) => {
      const context = buildToolContext();
      const current = activeToolRef.current;
      if (!context || !current) return;
      const toolEvent = toToolEvent(event, context);
      if (!toolEvent) return;
      pick(current)?.(toolEvent, context);
    },
    [buildToolContext, toToolEvent],
  );

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (event.altKey !== altHeld) setAltHeld(event.altKey);
    // Capture keeps the stroke alive when the pointer leaves the canvas; it can
    // legitimately fail for synthetic or already-released pointers.
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      /* not fatal */
    }
    if (event.button === 1 || spaceHeldRef.current) {
      panDragRef.current = { x: event.clientX, y: event.clientY };
      return;
    }
    if (event.button !== 0) return;
    if (grabGuide(event)) return;
    paintingRef.current = true;
    dispatch(event, (t) => t.onPointerDown);
  };

  /**
   * The Move tool doubles as the guide tool, the way Photoshop's does: a press
   * near a guide drags it instead of reaching the tool.
   */
  const grabGuide = (event: React.PointerEvent<HTMLCanvasElement>): boolean => {
    const store = useAppStore.getState();
    if (store.tool !== 'move' || !store.showGuides || store.guidesLocked) return false;
    const context = buildToolContext();
    if (!context || context.doc.guides.length === 0) return false;
    const toolEvent = toToolEvent(event, context);
    if (!toolEvent) return false;

    const guide = guideAt(context.doc.guides, toolEvent.point, GUIDE_GRAB / context.view.zoom);
    if (!guide) return false;
    guideDragRef.current = guide;
    beginMetaTransaction();
    return true;
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (event.altKey !== altHeld) setAltHeld(event.altKey);

    const panStart = panDragRef.current;
    if (panStart) {
      panBy(event.clientX - panStart.x, event.clientY - panStart.y);
      panDragRef.current = { x: event.clientX, y: event.clientY };
      return;
    }

    const context = buildToolContext();
    if (!context) return;
    const toolEvent = toToolEvent(event, context);
    if (!toolEvent) return;

    useHudStore.getState().setCursor(toolEvent.point);

    const dragged = guideDragRef.current;
    if (dragged) {
      moveGuideTransient(
        dragged.id,
        dragged.axis === 'horizontal' ? toolEvent.point.y : toolEvent.point.x,
      );
      return;
    }

    activeToolRef.current?.onPointerMove?.(toolEvent, context);
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (panDragRef.current) {
      panDragRef.current = null;
      return;
    }
    const dragged = guideDragRef.current;
    if (dragged) {
      guideDragRef.current = null;
      // Dropping a guide outside the canvas deletes it, as Photoshop does.
      const doc = getActiveDocument(useAppStore.getState());
      const guide = doc?.guides.find((entry) => entry.id === dragged.id);
      const extent = dragged.axis === 'horizontal' ? (doc?.height ?? 0) : (doc?.width ?? 0);
      const dropped = guide && (guide.position < 0 || guide.position > extent);
      if (dropped) removeGuideTransient(dragged.id);
      commitMetaTransaction(dropped ? 'Delete Guide' : 'Move Guide');
      return;
    }
    if (!paintingRef.current) return;
    paintingRef.current = false;
    dispatch(event, (t) => t.onPointerUp);
  };

  const handleDoubleClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    dispatch(
      {
        clientX: event.clientX,
        clientY: event.clientY,
        shiftKey: event.shiftKey,
        altKey: event.altKey,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        button: event.button,
      },
      (t) => t.onDoubleClick,
    );
  };

  const handlePointerLeave = () => {
    liveRender.brushCursor = null;
    useHudStore.getState().setCursor(null);
    engine.invalidate('overlay');
  };

  useEffect(() => {
    const canvas = overlayRef.current;
    if (!canvas) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const anchor = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      const store = useAppStore.getState();
      const doc = getActiveDocument(store);
      const view = doc ? store.views[doc.id] : null;
      if (!view) return;

      // Photoshop: Alt+wheel zooms; plain wheel pans the view.
      if (event.altKey) {
        zoomStep(event.deltaY < 0 ? 1 : -1, anchor);
      } else {
        panBy(-event.deltaX, -event.deltaY);
      }
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target, event)) return;

      // Claim app chords in the capture phase so the browser cannot open a new
      // tab on Ctrl+T (or similar) before our bubble handler runs.
      if (isAppChord(event)) event.preventDefault();

      // A modal owns the keyboard, except Escape / chords we already claimed.
      if (useAppStore.getState().dialog) {
        if (handleKeymap(event)) event.preventDefault();
        return;
      }

      if (event.code === 'Space' && !event.repeat) {
        spaceHeldRef.current = true;
        setSpaceHeld(true);
      }
      if (event.key === 'Alt') setAltHeld(true);
      const context = buildToolContext();
      if (context && activeToolRef.current?.onKeyDown?.(event, context)) {
        event.preventDefault();
        return;
      }
      if (handleKeymap(event)) event.preventDefault();
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Alt') {
        setAltHeld(false);
        // Leaving eyedropper mode restores the tip ring on the next move.
        if (ALT_SAMPLE_TOOLS.has(useAppStore.getState().tool)) {
          liveRender.brushCursor = null;
          getEngine().invalidate('overlay');
        }
      }
      if (event.code !== 'Space') return;
      spaceHeldRef.current = false;
      setSpaceHeld(false);
    };
    const onPaste = (event: ClipboardEvent) => {
      const file = Array.from(event.clipboardData?.files ?? []).find((f) =>
        f.type.startsWith('image/'),
      );
      if (!file) return;
      event.preventDefault();
      // Something newer is on the clipboard, so the in-app copy is stale.
      setInternalClipboard(null);
      void placeImageAsLayer(file);
    };

    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('paste', onPaste);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('paste', onPaste);
    };
  }, [buildToolContext]);

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDropActive(false);
    const files = droppedFiles(event.dataTransfer);
    if (files.length > 0) void acceptDroppedFile(files[0]);
  };

  const cursor = spaceHeld
    ? 'grab'
    : altHeld && ALT_SAMPLE_TOOLS.has(tool)
      ? 'crosshair'
      : (TOOLS[tool]?.cursor ?? 'default');

  const hasDoc = Boolean(doc);
  const hasSelection = Boolean(doc?.hasSelection);
  const [toolSections, setToolSections] = useState(() =>
    buildToolContextSections(tool, useAppStore.getState()),
  );

  return (
    <ContextMenu.Root
      onOpenChange={(open) => {
        if (open) {
          setToolSections(buildToolContextSections(tool, useAppStore.getState()));
        }
      }}
    >
      <ContextMenu.Trigger asChild disabled={!hasDoc}>
        <div
          className={`canvas-view${dropActive ? ' canvas-view--drop' : ''}`}
          ref={containerRef}
          onDragOver={(event) => {
            event.preventDefault();
            setDropActive(true);
          }}
          onDragLeave={() => setDropActive(false)}
          onDrop={handleDrop}
        >
          <canvas className="canvas-view__gl" ref={glCanvasRef} />
          <canvas
            className="canvas-view__overlay"
            ref={overlayRef}
            style={{ cursor }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onPointerLeave={handlePointerLeave}
            onDoubleClick={handleDoubleClick}
          />
          {showRulers && doc && view && (
            <Rulers
              doc={doc}
              view={view}
              size={canvasSize}
              devicePixelRatio={engine.devicePixelRatio}
              cursor={hudCursor}
            />
          )}
          <TextEditOverlay canvasSize={canvasSize} />
          {dropActive && <div className="canvas-view__drop-hint">Drop to open the file</div>}
        </div>
      </ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Content className="menu-content" collisionPadding={8}>
          <ToolContextMenuItems sections={toolSections} />
          {hasSelection && (
            <>
              <ContextMenu.Item
                className="menu-item"
                onSelect={() => beginTransform('free')}
              >
                <span>Free Transform</span>
                <span className="menu-item__shortcut">{commandChord('edit.transform')}</span>
              </ContextMenu.Item>
              <ContextMenu.Sub>
                <ContextMenu.SubTrigger className="menu-item">
                  <span>Transform</span>
                  <span className="menu-item__shortcut">▸</span>
                </ContextMenu.SubTrigger>
                <ContextMenu.Portal>
                  <ContextMenu.SubContent className="menu-content" sideOffset={4}>
                    {TRANSFORM_MODES.map(({ mode, label }) => (
                      <ContextMenu.Item
                        key={mode}
                        className="menu-item"
                        onSelect={() => beginTransform(mode)}
                      >
                        <span>{label}</span>
                      </ContextMenu.Item>
                    ))}
                    <ContextMenu.Separator className="menu-separator" />
                    {QUICK_TRANSFORMS.map(({ kind, label }) => (
                      <ContextMenu.Item
                        key={kind}
                        className="menu-item"
                        onSelect={() => applyQuickTransform(kind)}
                      >
                        <span>{label}</span>
                      </ContextMenu.Item>
                    ))}
                  </ContextMenu.SubContent>
                </ContextMenu.Portal>
              </ContextMenu.Sub>
              <ContextMenu.Item
                className="menu-item"
                onSelect={startTransformSelection}
              >
                <span>Transform Selection</span>
                <span className="menu-item__shortcut">{commandChord('select.transform')}</span>
              </ContextMenu.Item>
              <ContextMenu.Item className="menu-item" onSelect={cropToSelection}>
                <span>Crop</span>
              </ContextMenu.Item>
              <ContextMenu.Separator className="menu-separator" />
            </>
          )}
          <ContextMenu.Item
            className="menu-item"
            disabled={!hasSelection}
            onSelect={deselect}
          >
            <span>Deselect</span>
            <span className="menu-item__shortcut">{commandChord('select.none')}</span>
          </ContextMenu.Item>
          <ContextMenu.Item
            className="menu-item"
            disabled={!hasDoc}
            onSelect={selectAll}
          >
            <span>Select All</span>
            <span className="menu-item__shortcut">{commandChord('select.all')}</span>
          </ContextMenu.Item>
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}
